import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { auth } from "../../firebase";
import logo from "../../assets/logo.png";
import { useAuth } from "../../context/AuthContext";
import { can, isStaff } from "../../utils/roles";
import { useChat } from "../../context/ChatContext";
import { supportChatEnabled, salesReportsEnabled, contentMenuEnabled } from "../../utils/features";
import { FaUser, FaSuitcase, FaChartBar } from "react-icons/fa";

const SALES_LINKS = [
  { label: "Daily Sales Report", path: "/sales-reports/daily-sales", group: "agency" },
  { label: "PNR Audit Trail",    path: "/sales-reports/audit-trail",  group: "agency" },
  { label: "Sales Summary",      path: "/sales-reports/asr/sales-summary",    group: "asr" },
  { label: "Station Summary",    path: "/sales-reports/asr/station-summary",  group: "asr" },
  { label: "Agent Summary",      path: "/sales-reports/asr/agent-summary",    group: "asr" },
  { label: "Accounting",         path: "/sales-reports/asr/accounting",       group: "asr" },
  { label: "Document Detail",    path: "/sales-reports/asr/document-detail",  group: "asr" },
  { label: "Credit Card",        path: "/sales-reports/asr/credit-card",      group: "asr" },
];

const Navbar = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const { profile, profileLoading } = useAuth();
  const { unreadTotal: inboxUnread } = useChat();
  const isAgent = isStaff(profile);

  const [showDropdown, setShowDropdown]         = useState(false);
  const dropdownRef                             = useRef<HTMLDivElement>(null);
  const [showSalesMenu, setShowSalesMenu]       = useState(false);
  const salesMenuRef                            = useRef<HTMLDivElement>(null);
  const [showMobileMenu, setShowMobileMenu]     = useState(false);
  const [showMobileSales, setShowMobileSales]   = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return unsubscribe;
  }, []);

  // Close user dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  // Close sales menu on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (salesMenuRef.current && !salesMenuRef.current.contains(event.target as Node)) {
        setShowSalesMenu(false);
      }
    }
    if (showSalesMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSalesMenu]);

  const closeDropdownAndNavigate = (path: string) => {
    setShowDropdown(false);
    navigate(path);
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    navigate("/");
  };

  // User dropdown items (My Profile, My Trips, Logout)
  const MenuItems = ({ onNavigate }: { onNavigate?: (p: string) => void }) => {
    const nav = onNavigate || closeDropdownAndNavigate;
    return (
      <>
        <div
          className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
          onClick={() => nav("/profile")}
        >
          <FaUser className="text-primary text-sm" />
          <div className="text-heading font-semibold text-base">My Profile</div>
        </div>
        <div
          className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
          onClick={() => nav("/my-trips")}
        >
          <FaSuitcase className="text-primary text-sm" />
          <div className="text-heading font-semibold text-base">My Trips</div>
        </div>
        <div
          className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
          onClick={async () => {
            await handleLogout();
            if (onNavigate) onNavigate("/");
          }}
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="text-primary">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16,17 21,12 16,7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          <div className="text-heading font-semibold text-base">Logout</div>
        </div>
      </>
    );
  };

  const agencyLinks = SALES_LINKS.filter((l) => l.group === "agency");
  const asrLinks    = SALES_LINKS.filter((l) => l.group === "asr");

  return (
    <header className="w-auto border-b border-black bg-white">
      <div className="max-w-screen-xl mx-auto flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <img
            src={logo}
            onClick={() => navigate("/")}
            alt="BuildTravel"
            className="w-[234.34px] cursor-pointer"
          />
        </div>

        {/* Desktop controls */}
        <div className="hidden md:flex items-center justify-end gap-4 font-sans text-base text-heading">

          {/* The staff and admin menus are drawn from `permissions`, which
              arrives on a second request after sign-in. Until it lands every
              can() below answers false, so the bar would render its
              signed-out shape and then visibly rearrange itself as the menus
              appeared. This holds the space until the answer is known. */}
          {profileLoading && (
            <div className="flex items-center gap-4" aria-hidden="true">
              <div className="h-4 w-24 rounded bg-gray-200 animate-pulse" />
              <div className="h-4 w-20 rounded bg-gray-200 animate-pulse" />
              <div className="h-4 w-16 rounded bg-gray-200 animate-pulse" />
            </div>
          )}

          {/* ── Sabre report screens ── */}
          {/* These read the agency's whole commercial record. Behind the
              `reports` module, so the menu appears only where the server would
              actually answer — showing a door that does not open helps nobody. */}
          {can(profile, "reports") && salesReportsEnabled && (
            <div className="relative" ref={salesMenuRef}>
              <button
                onClick={() => setShowSalesMenu((p) => !p)}
                className="flex items-center gap-1.5 font-semibold text-heading hover:text-primary transition text-base"
              >
                <FaChartBar className="text-sm" />
                <span>Sales Reports</span>
                <svg
                  width="12" height="12" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5"
                  className={`transition-transform duration-150 ${showSalesMenu ? "rotate-180" : ""}`}
                >
                  <polyline points="6,9 12,15 18,9" />
                </svg>
              </button>

              {showSalesMenu && (
                <div className="absolute left-0 top-10 bg-white rounded-xl shadow-xl border border-gray-100 w-60 z-[70] py-2">
                  <div className="text-xs uppercase text-gray-400 font-bold px-3 py-1.5 tracking-widest">
                    Agency Reports
                  </div>
                  {agencyLinks.map((l) => (
                    <div
                      key={l.path}
                      className="px-3 py-2 cursor-pointer hover:bg-gray-50 text-heading font-medium text-sm rounded-md mx-1"
                      onClick={() => { setShowSalesMenu(false); navigate(l.path); }}
                    >
                      {l.label}
                    </div>
                  ))}

                  <div className="border-t border-gray-100 my-1.5 mx-3" />

                  <div className="text-xs uppercase text-gray-400 font-bold px-3 py-1.5 tracking-widest">
                    ASR — SabreSonic
                  </div>
                  {asrLinks.map((l) => (
                    <div
                      key={l.path}
                      className="px-3 py-2 cursor-pointer hover:bg-gray-50 text-heading font-medium text-sm rounded-md mx-1"
                      onClick={() => { setShowSalesMenu(false); navigate(l.path); }}
                    >
                      {l.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Markup Management (super user only) ── */}
          {can(profile, "markup") && (
            <button
              onClick={() => navigate("/admin/markup")}
              className="flex items-center gap-1.5 font-semibold text-heading hover:text-primary transition text-base"
            >
              <span>Markup</span>
            </button>
          )}

          {/* ── Content Studio (super user only) ── */}
          {can(profile, "content") && contentMenuEnabled && (
            <button
              onClick={() => navigate("/admin/content")}
              className="flex items-center gap-1.5 font-semibold text-heading hover:text-primary transition text-base"
            >
              <span>Content</span>
            </button>
          )}

          {/* ── Staff accounts (super user only) ── */}
          {can(profile, "staff") && (
            <button
              onClick={() => navigate("/admin/staff")}
              className="flex items-center gap-1.5 font-semibold text-heading hover:text-primary transition text-base"
            >
              <span>Staff</span>
            </button>
          )}

          {/* ── Branches (super user only) ── */}
          {can(profile, "branches") && (
            <button
              onClick={() => navigate("/admin/branches")}
              className="flex items-center gap-1.5 font-semibold text-heading hover:text-primary transition text-base"
            >
              <span>Branches</span>
            </button>
          )}

          {/* ── Sales dashboard (super user only) ── */}
          {can(profile, "dashboard") && (
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="flex items-center gap-1.5 font-semibold text-heading hover:text-primary transition text-base"
            >
              <span>Sales</span>
            </button>
          )}

          {/* ── Permission groups ── */}
          {can(profile, "groups") && (
            <button
              onClick={() => navigate("/admin/groups")}
              className="flex items-center gap-1.5 font-semibold text-heading hover:text-primary transition text-base"
            >
              <span>Groups</span>
            </button>
          )}

          {/* ── Cash approvals ── */}
          {can(profile, "approvals") && (
            <button
              onClick={() => navigate("/admin/approvals")}
              className="flex items-center gap-1.5 font-semibold text-heading hover:text-primary transition text-base"
            >
              <span>Approvals</span>
            </button>
          )}

          {/* ── Settings ── */}
          {can(profile, "settings") && (
            <button
              onClick={() => navigate("/admin/settings")}
              className="flex items-center gap-1.5 font-semibold text-heading hover:text-primary transition text-base"
            >
              <span>Settings</span>
            </button>
          )}

          {/* ── Support Inbox (staff, and only while support chat is on) ── */}
          {isAgent && supportChatEnabled && (
            <button
              onClick={() => navigate("/support-inbox")}
              className="relative flex items-center gap-1.5 font-semibold text-heading hover:text-primary transition text-base"
            >
              <span>Inbox</span>
              {inboxUnread > 0 && (
                <span className="min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5 ml-0.5">
                  {inboxUnread > 9 ? "9+" : inboxUnread}
                </span>
              )}
            </button>
          )}

          {/* ── Saved passengers (module-gated) ── */}
          {can(profile, "passengers") && (
            <button
              onClick={() => navigate("/passengers")}
              className="flex items-center gap-1.5 font-semibold text-heading hover:text-primary transition text-base"
            >
              <span>Passengers</span>
            </button>
          )}

          {/* ── User avatar + dropdown ── */}
          {user ? (
            <div className="relative flex items-center gap-2" ref={dropdownRef}>
              <img
                src={
                  user.photoURL ||
                  "https://ui-avatars.com/api/?name=" +
                    encodeURIComponent(user.displayName || user.email || "User")
                }
                alt="avatar"
                className="w-8 h-8 rounded-full border cursor-pointer"
                onClick={() => setShowDropdown((p) => !p)}
              />
              <span
                className="font-semibold cursor-pointer text-base text-heading"
                onClick={() => setShowDropdown((p) => !p)}
              >
                {user.displayName || user.email}
              </span>

              {showDropdown && (
                <div className="absolute right-0 top-12 bg-white rounded-lg shadow-lg border w-60 z-[70] p-2">
                  <MenuItems />
                </div>
              )}
            </div>
          ) : (
            <button
              className="bg-primary text-white font-semibold px-4 py-1.5 rounded-full hover:bg-blue-800 transition text-base"
              onClick={() => navigate("/login")}
            >
              Login / Sign Up
            </button>
          )}
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden flex items-center gap-2">
          <button
            aria-label="Toggle menu"
            onClick={() => setShowMobileMenu((s) => !s)}
            className="p-2 rounded-md border"
          >
            {showMobileMenu ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {showMobileMenu && (
        <div className="md:hidden border-t border-black bg-white px-4 py-3">
          {user ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <img
                  src={
                    user.photoURL ||
                    "https://ui-avatars.com/api/?name=" +
                      encodeURIComponent(user.displayName || user.email || "User")
                  }
                  alt="avatar"
                  className="w-10 h-10 rounded-full border"
                />
                <div className="font-semibold text-heading text-base">
                  {user.displayName || user.email}
                </div>
              </div>

              {/* Saved passengers (module-gated) */}
              {can(profile, "passengers") && (
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
                  onClick={() => { setShowMobileMenu(false); navigate("/passengers"); }}
                >
                  <span className="font-semibold text-heading text-base">Passengers</span>
                </div>
              )}

              {/* Markup Management (super user only) */}
              {can(profile, "markup") && (
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
                  onClick={() => { setShowMobileMenu(false); navigate("/admin/markup"); }}
                >
                  <span className="font-semibold text-heading text-base">Markup Management</span>
                </div>
              )}

              {/* Content Studio (super user only) */}
              {can(profile, "content") && contentMenuEnabled && (
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
                  onClick={() => { setShowMobileMenu(false); navigate("/admin/content"); }}
                >
                  <span className="font-semibold text-heading text-base">Content Studio</span>
                </div>
              )}

              {/* Staff accounts (super user only) */}
              {can(profile, "staff") && (
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
                  onClick={() => { setShowMobileMenu(false); navigate("/admin/staff"); }}
                >
                  <span className="font-semibold text-heading text-base">Staff Accounts</span>
                </div>
              )}

              {/* Branches (super user only) */}
              {can(profile, "branches") && (
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
                  onClick={() => { setShowMobileMenu(false); navigate("/admin/branches"); }}
                >
                  <span className="font-semibold text-heading text-base">Branches</span>
                </div>
              )}

              {/* Sales dashboard (super user only) */}
              {can(profile, "dashboard") && (
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
                  onClick={() => { setShowMobileMenu(false); navigate("/admin/dashboard"); }}
                >
                  <span className="font-semibold text-heading text-base">Sales</span>
                </div>
              )}

              {/* Permission groups */}
              {can(profile, "groups") && (
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
                  onClick={() => { setShowMobileMenu(false); navigate("/admin/groups"); }}
                >
                  <span className="font-semibold text-heading text-base">Groups</span>
                </div>
              )}

              {/* Cash approvals */}
              {can(profile, "approvals") && (
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
                  onClick={() => { setShowMobileMenu(false); navigate("/admin/approvals"); }}
                >
                  <span className="font-semibold text-heading text-base">Cash Approvals</span>
                </div>
              )}

              {/* Settings */}
              {can(profile, "settings") && (
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
                  onClick={() => { setShowMobileMenu(false); navigate("/admin/settings"); }}
                >
                  <span className="font-semibold text-heading text-base">Settings</span>
                </div>
              )}

              {/* Support Inbox (staff, and only while support chat is on) */}
              {isAgent && supportChatEnabled && (
                <div
                  className="flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
                  onClick={() => { setShowMobileMenu(false); navigate("/support-inbox"); }}
                >
                  <span className="font-semibold text-heading text-base">Support Inbox</span>
                  {inboxUnread > 0 && (
                    <span className="min-w-[20px] h-[20px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1.5">
                      {inboxUnread > 9 ? "9+" : inboxUnread}
                    </span>
                  )}
                </div>
              )}

              {/* Sabre report screens — matches the server's `reports` module. */}
              {can(profile, "reports") && salesReportsEnabled && (
                <div className="bg-white rounded-lg shadow-sm border w-full">
                  <button
                    onClick={() => setShowMobileSales((p) => !p)}
                    className="flex items-center justify-between w-full px-3 py-2.5 font-semibold text-heading text-base"
                  >
                    <div className="flex items-center gap-2">
                      <FaChartBar className="text-primary text-sm" />
                      Sales Reports
                    </div>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      className={`transition-transform duration-150 ${showMobileSales ? "rotate-180" : ""}`}>
                      <polyline points="6,9 12,15 18,9" />
                    </svg>
                  </button>
                  {showMobileSales && (
                    <div className="border-t border-gray-100 px-2 pb-2">
                      <div className="text-xs uppercase text-gray-400 font-bold px-2 py-1.5 tracking-widest">Agency Reports</div>
                      {agencyLinks.map((l) => (
                        <div key={l.path}
                          className="px-2 py-2 cursor-pointer hover:bg-gray-50 text-heading font-medium text-sm rounded"
                          onClick={() => { setShowMobileSales(false); setShowMobileMenu(false); navigate(l.path); }}
                        >{l.label}</div>
                      ))}
                      <div className="border-t border-gray-100 my-1 mx-1" />
                      <div className="text-xs uppercase text-gray-400 font-bold px-2 py-1.5 tracking-widest">ASR — SabreSonic</div>
                      {asrLinks.map((l) => (
                        <div key={l.path}
                          className="px-2 py-2 cursor-pointer hover:bg-gray-50 text-heading font-medium text-sm rounded"
                          onClick={() => { setShowMobileSales(false); setShowMobileMenu(false); navigate(l.path); }}
                        >{l.label}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* User menu items */}
              <div className="bg-white rounded-lg shadow-sm border w-full z-[60] p-2">
                <MenuItems
                  onNavigate={(path: string) => {
                    setShowMobileMenu(false);
                    navigate(path);
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div
                className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded p-2"
                onClick={() => { setShowMobileMenu(false); navigate("/login"); }}
              >
                <div className="text-heading font-semibold text-base">Login / Sign Up</div>
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default Navbar;
